import React, { useEffect, useRef, useState } from 'react';
import styled, { createGlobalStyle, css } from 'styled-components';
import { useKey } from 'react-use';
import { Text } from '@lmn/design-system';

import { IconArrowLeftLinear } from '@/app/common/icons/libraryIcons';

import { useBrowserBackButton } from '@/app/components/hotel-detail/hooks/useBrowserBackButton';
import { useFocusTrap } from '@/app/common/hooks/useFocusTrap';
import { useIsDesktopOrGreater, useIsTabletOrGreater } from '@/app/components/hotel-detail/hooks/useMediaSSR';
import { useOffersContext } from '@/app/components/hotel-detail/OffersProvider';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';

import { type ResponsiveOptions, getImageSources } from '@/app/components/hotel-detail/getGallerySourceOptimization';
import { isImageUrlTransformable } from '@/app/components/search/utils/images.ts';

import { Picture } from '@/app/components/hotel-detail/ImageWithFallback';

import customCss from '@/app/components/hotel-detail/css/customCss';
import { useCuriosity } from '@/app/components/hotel-detail/CuriosityProvider';

import { HotelGalleryGoToRoomsButton } from './GoToRoomsButton';
import ImageGalleryModalX from './ImageGalleryModalX';

type ImageGalleryPageLayoutProps = {
  navigateToRooms?: boolean;
  images: readonly string[];
  initialImageIndex?: number;
  name: string;
  isOpen: boolean | undefined;
  onDismiss?: (event?: React.SyntheticEvent<Element, Event>) => void;
};

type OverlayStateType = { isOpen: boolean | undefined; initialFocusedImageIndex: number };

const GlobalStyle = createGlobalStyle`
body {
  overflow: hidden;
}
`;

export default function ImageGalleryPageLayout(props: ImageGalleryPageLayoutProps) {
  const { images, name, isOpen, initialImageIndex, onDismiss, navigateToRooms = false, ...rest } = props;
  const { offersData } = useOffersContext();
  const {
    commonData: { isApp },
  } = usePageWrapperData();
  const isMobile = !useIsTabletOrGreater();
  const isDesktop = useIsDesktopOrGreater();
  const amountOfImages = images.length;
  const showBookButton =
    navigateToRooms && offersData?.noAvailableRooms !== true && offersData?.noAvailableFlights !== true;
  const { sendCuriosityClickEvent } = useCuriosity();
  const [modalState, setModalState] = useState<OverlayStateType>({ isOpen: undefined, initialFocusedImageIndex: 0 });
  // Ref to be used in event handlers. A way to have the current state, in handlers if you check state, you'll get the initial state.
  const isOpenRef = useRef(false);
  const triggerBook = useRef(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useFocusTrap({ active: !!isOpen, getContainer: () => rootRef.current });

  const onDismissHandler = () => {
    onDismiss?.();
  };

  const openModal = () => {
    isOpenRef.current = true;
    setModalState((prev) => ({ ...prev, isOpen: true }));
  };

  const openModalAtIndex = (index: number) => {
    isOpenRef.current = true;
    setModalState({ isOpen: true, initialFocusedImageIndex: index });
  };

  const closeModal = () => {
    isOpenRef.current = false;
    setModalState((prev) => ({ ...prev, isOpen: false }));
  };

  useKey('Escape', () => {
    if (isOpenRef.current) {
      onDetailsModalDismissHandler();
    } else {
      onDismissHandler();
    }
  });

  useEffect(() => {
    const element = document.querySelector<HTMLElement>('.initial_selected');
    element?.scrollIntoView();
  }, [initialImageIndex]);

  useBrowserBackButton({
    condition: modalState.isOpen,
    modalId: 'gallery-image-details',
    onClose: closeModal,
    onOpen: openModal,
    onFinallyHistoryBack: () => {
      if (triggerBook.current) {
        onBookButtonClick();
        triggerBook.current = false;
      }
    },
  });

  const onClickHandler = (initialFocusedImageIndex: number) => {
    sendCuriosityClickEvent({
      event_name: 'click_gallery_image',
      event_context: 'gallery',
      event_value: `image_num_${initialFocusedImageIndex}`,
    });
    openModalAtIndex(initialFocusedImageIndex);
  };

  const onDetailsModalDismissHandler = () => {
    closeModal();
  };

  const onDetailsModalShowRoomsClick = () => {
    triggerBook.current = true;
    closeModal();
  };

  const onBookButtonClick = () => {
    onDismiss?.();
  };

  return (
    <div
      {...rest}
      ref={rootRef}
      tabIndex={-1}
      inert={!isOpen}
      css={css`
        position: fixed;
        inset: 0;
        display: flex;
        flex-direction: column;
        background: white;
        transform: ${isOpen ? 'translate(0, 0)' : 'translate(0, 100%)'};
        opacity: ${isOpen ? 1 : 0};
        transition: all 0.3s ease-in-out;
        z-index: 1000;
      `}
    >
      {isOpen && <GlobalStyle />}
      <div
        css={css`
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: ${({ theme }) => theme.kallisto.shadow.mediumDownShadow};
          height: 56px;
          padding: 1rem;
          background: white;
        `}
      >
        {!isApp && <GoBackButton onClick={onDismissHandler} onKeyPress={onDismissHandler} />}
        <Text
          variant="Title4"
          forceTag="div"
          css={css`
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 85%;
            white-space: nowrap;
          `}
        >
          {name}
        </Text>
        {showBookButton && isDesktop && (
          <div
            css={`
              display: flex;
              align-items: center;
              justify-content: center;
              position: absolute;
              right: 16px;
            `}
          >
            <HotelGalleryGoToRoomsButton
              onClick={onBookButtonClick}
              css={`
                margin-left: 24px;
              `}
            />
          </div>
        )}
      </div>
      <div
        css={css`
          width: 100%;
          padding: 16px 16px ${showBookButton ? 0 : '16px'};
          display: flex;
          flex-direction: column;
          overflow: auto;
          overscroll-behavior: none;

          @media ${customCss.breakpoints.xl} {
            padding: 16px;
            margin-bottom: 0;
          }
        `}
      >
        <div
          css={css`
            display: grid;
            gap: 1vw;
            grid-template-rows: 30vw;
            grid-template-columns: repeat(2, 1fr);

            @media ${customCss.breakpoints.xl} {
              gap: 0.5rem;
              grid-template-rows: 318px;
              margin: 0 auto;
              width: 1000px;
            }
          `}
        >
          {isOpen &&
            images?.map((src, i) => {
              const [fullWidth, fullHeight] = getImageCssSize(i, amountOfImages);

              const width = fullWidth ? 1000 : 496;
              const height = fullHeight ? 644 : 318;

              const responsiveOptions: ResponsiveOptions = [
                {
                  media: customCss.breakpoints.lg,
                  imageWidth: width,
                  imageHeight: height,
                  bookingResolution: 'max1920x1200',
                },
                {
                  media: customCss.breakpoints.sm,
                  imageWidth: fullWidth ? 860 : 420,
                  imageHeight: fullHeight ? 540 : 270,
                  bookingResolution: 'max1280x900',
                },
                {
                  media: customCss.breakpoints.xs,
                  imageWidth: fullWidth ? 568 : 280,
                  imageHeight: fullHeight ? 366 : 180,
                  bookingResolution: 'max1024x768',
                },
                {
                  media: customCss.breakpoints.xxs,
                  imageWidth: fullWidth ? 340 : 160,
                  imageHeight: fullHeight ? 220 : 110,
                  bookingResolution: 'max500',
                },
              ];

              // Note: Picture is not taking into account device dpr, so if we configure one image width for
              // a specific breakpoint (screen width), it will always load that image width no matter device dpr.
              //
              // For example is the screen width is 340px, and we have:
              //  (min-width: 300px) 300w, (min-width: 500px) 500w, (min-with: 800px) 1000w
              //
              // The browser will choose always the 300w image for all the dprs.
              //
              //
              // If we decide to use ImageWithFallback instead, the dpr is taken into account. It means that
              // if we are using a 2.0 dpr device and screen width is 340px the browser will search for 680px
              // best suitable configuration in the sizes and srcSet configuration
              //
              // For example is the screen width is 340px, and we have:
              //  (min-width: 300px) 300w, (min-width: 500px) 500w, (min-with: 800px) 1000w
              //
              // The browser will choose the 300w image for dpr 1.0, 500w for dpr 2.0 and 1000w for dpr 3.0.
              //
              //
              // To use ImageWithFallback is needed to change Picture url and sources properties by:
              //    url={getImageSrc({
              //      url: src,
              //      responsiveOptions,
              //      mediaQuery: isMobile ? customCss.breakpoints.xxs : customCss.breakpoints.lg,
              //    })}
              //    srcSet={getImageSrcSet({ url: src, responsiveOptions })}
              //    sizes={getImageSizes({ url: src, responsiveOptions })}
              return (
                <Picture
                  className={initialImageIndex === i && i !== 0 ? 'initial_selected' : ''}
                  tabIndex={0}
                  key={src}
                  url={src}
                  sources={getImageSources({ url: src, responsiveOptions })}
                  alt={`gallery image ${i}`}
                  // @ts-ignore
                  loading={!isImageUrlTransformable(src) || i > 49 || isMobile ? 'lazy' : 'auto'}
                  onClick={() => onClickHandler(i)}
                  onKeyPress={() => onClickHandler(i)}
                  pictureCss={css`
                    ${fullHeight ? 'grid-row: auto / span 2;' : ''}
                    ${fullWidth ? 'grid-column: auto / span 2;' : ''}
                    width: 100%;
                    height: ${fullHeight ? 61 : 30}vw;
                    @media ${customCss.breakpoints.xl} {
                      height: ${height}px;
                    }
                  `}
                  css={css`
                    width: 100%;
                    height: 100%;
                    cursor: pointer;
                    border-radius: 4px;
                    object-fit: cover;
                    background: ${({ theme }) => theme.commons.colors.gray100};
                    -webkit-tap-highlight-color: transparent;
                    ${initialImageIndex === i && i !== 0 ? 'scroll-margin: 24px;' : ''}
                  `}
                />
              );
            })}
        </div>
      </div>
      {!isDesktop && showBookButton && (
        <div
          css={`
            background: white;
            padding: 12px 16px;
          `}
        >
          <HotelGalleryGoToRoomsButton
            onClick={onBookButtonClick}
            css={`
              width: 100%;
            `}
          />
        </div>
      )}
      <ImageGalleryModalX
        images={images}
        isOpen={modalState.isOpen}
        initialFocussedImageIndex={modalState.initialFocusedImageIndex}
        navigateToRooms={navigateToRooms}
        onDismiss={onDetailsModalDismissHandler}
        onShowRoomsClick={onDetailsModalShowRoomsClick}
      />
    </div>
  );
}

/**
 *  Images grid format:
 *
 *  --- 1 ---
 *  -2-   -3-
 *  -2-   -4-
 *  -5-   -6-
 *  -7-   -6-
 *  --- 8 ---
 *  -9-   -10-
 *
 * ... and repeat
 *
 * Special cases:
 *
 * Below 4 images they are shown in full width;
 *
 *  --- 1 ---
 *  --- 2 ---
 *
 *  --- 1 ---
 *  -2-   -3-
 *
 *  --- 1 ---
 *  -2-   -3-
 *  -2-   -4-
 *
 *  --- 1 ---
 *  -2-   -3-
 *  -4-   -5-
 *
 *  --- 1 ---
 *  -2-   -3-
 *  -2-   -4-
 *  -5-   -6-
 *
 *  --- 1 ---
 *  -2-   -3-
 *  -2-   -4-
 *  -5-   -6-
 *  --- 7 ---
 *
 *  --- 1 ---
 *  -2-   -3-
 *  -2-   -4-
 *  -5-   -6-
 *  -7-   -6-
 *  --- 8 ---
 *
 *  --- 1 ---
 *  -2-   -3-
 *  -2-   -4-
 *  -5-   -6-
 *  -7-   -6-
 *  -8-   -9-
 */
function getImageCssSize(imageIndex: number, totalImages: number): [boolean, boolean] {
  const reminder = (imageIndex + 1) % 10;
  const reminderTotal = totalImages % 10;
  const pageIndex = Math.floor(imageIndex / 10);
  const lastPageIndex = Math.floor(totalImages / 10);

  if (
    totalImages < 4 ||
    reminder === 1 ||
    (reminder === 2 && reminderTotal === 2 && pageIndex > 0) ||
    (reminder === 7 && reminderTotal === 7 && imageIndex === totalImages - 1) ||
    (reminder === 8 && (reminderTotal === 8 || reminderTotal === 0 || pageIndex < lastPageIndex))
  ) {
    return [true, true];
  }

  if (
    (reminder === 2 &&
      (pageIndex < lastPageIndex || reminderTotal === 2 || reminderTotal === 4 || reminderTotal >= 6)) ||
    (reminder === 6 && (pageIndex < lastPageIndex || reminderTotal >= 8))
  ) {
    return [false, true];
  }

  return [false, false];
}

const GoBackButton = styled(IconArrowLeftLinear).attrs({
  role: 'button',
  tabIndex: 0,
  'aria-label': 'close',
  className: 'close-button',
  color: '#3c3c3c',
})`
  top: 19px;
  left: 16px;
  position: absolute;
  border-radius: 100%;
  width: 18px;
  height: 18px;
  cursor: pointer;

  @media ${customCss.breakpoints.xl} {
    left: 16px;
  }
`;
